import classnames from 'classnames';
import React, { useMemo } from 'react';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationGroup, Input, RadioGroup } from '@bingads-webui-react/primitive-validation';
import { Constants, TaxUtils } from '@bingads-webui-clientcenter/entity-utils';
import { InlineInfoAlert } from '@bingads-webui-cc-react/alert';
import { TaxTypeFormPropTypes } from '../shapes';
import { InputTaxFieldId, requiredFeatures } from '../constants';
import { registerTaxform } from '../taxform-store';
import { withViewTaxFormWithFilledEmptyTaxValue, withTaxFormByPilot, getHelpPopupTopicFromTaxSchema, getTaxTypeLabel } from '../utils';
import { CustomValidateItems, validateField } from '../validator';
import { EmptyTaxForm } from './empty-taxform';
import { EmptyViewTaxForm } from './viewtaxforms/empty-viewtaxform';
import { CLViewTaxForm } from './viewtaxforms/chile-viewtaxform';
import { useChileTaxformStyles } from '../styles';
import { useValidationI18n } from '../hooks/use-i18n';

const {
  CountryCode, TaxType, TaxInfo, ChileIVAorVATConfirmValueToLabel, ChileWHTConfirmValueToLabel, AttestationValues,
} = Constants;
const {
  isChileVATValid, normalizeAttestationValue,
} = TaxUtils;

const ChileTaxSchema = {
  name: 'Chile Taxation Validation Schema',
  type: 'object',
  properties: {
    [TaxType.VatNumber]: {
      type: 'string',
      required: false,
      helpPopupTopic: 'pop_BA_Accounts_RUTNumber_CL',
    },
    [TaxInfo.IsIVAOrVATTaxPayer]: {
      type: 'string',
      required: true,
    },
    [TaxInfo.IsWithholdingTaxExempted]: {
      type: 'string',
      helpPopupTopic: 'pop_BA_Accounts_WithholdingTax_CL',
      required: true,
    },
  },
};

const initChileTaxData = data => ({
  ...data,
  [TaxInfo.IsIVAOrVATTaxPayer]: normalizeAttestationValue(data[TaxInfo.IsIVAOrVATTaxPayer]),
  [TaxInfo.IsWithholdingTaxExempted]: normalizeAttestationValue(data[TaxInfo.IsWithholdingTaxExempted]),
});

const ChileTaxForm = ({
  i18n,
  alwaysShowError,
  data,
  disabled,
  onValidate,
  showLineByLineFormGroup,
}) => {
  const validationI18n = useValidationI18n(i18n);
  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const styles = useChileTaxformStyles();
  const initialTaxData = useMemo(() => initChileTaxData(data), [data]);

  return (
    <ValidationGroup
      validationProps={{
        i18n: validationI18n,
        onValidate,
        data: initialTaxData,
        schema: ChileTaxSchema,
      }}
    >
      <CustomizedFormGroup isStatic id="Account_ChileIVAorVATConfirmLabel" label={i18n.getString(_TL_('Please confirm whether you’re an IVA/VAT tax payer'))}>
        <div className={classnames(styles.flex, styles.gap20px)}>
          <RadioGroup
            i18n={i18n}
            items={AttestationValues}
            renderItem={itemValue => i18n.getString(ChileIVAorVATConfirmValueToLabel[itemValue])}
            name="ChileIVAorVATConfirmRadioGroup"
            validationProps={{ i18n: validationI18n, field: 'IsIVAOrVATTaxPayer', alwaysShowError }}
          />
        </div>
      </CustomizedFormGroup>
      <CustomizedFormGroup
        isStatic
        label={getTaxTypeLabel(TaxType.VatNumber, i18n, { countryCode: CountryCode.CL })}
        helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema: ChileTaxSchema, taxType: TaxType.VatNumber })}
      >
        <Input
          id={InputTaxFieldId(TaxType.VatNumber)}
          validationProps={{
            field: TaxType.VatNumber,
            alwaysShowError,
            showErrorOnPassiveMessages: true,
            validator: value => validateField(
              value,
              {},
              { [CustomValidateItems.ChileVatNumberValidation]: inputValue => !isChileVATValid(inputValue) },
              i18n
            ),
          }}
          disabled={disabled}
        />
        <InlineInfoAlert>{i18n.getString(_TL_('Failed to provide your RUT number may result in tax being applied to your invoice.'))}</InlineInfoAlert>
      </CustomizedFormGroup>
      <CustomizedFormGroup
        isStatic
        id="Account_ChileWHTConfirmLabel"
        label={i18n.getString(_TL_('Please confirm whether the Withholding Tax(WHT) applied to you'))}
        helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema: ChileTaxSchema, taxType: TaxInfo.IsWithholdingTaxExempted })}
      >
        <div className={classnames(styles.flex, styles.gap20px)}>
          <RadioGroup
            i18n={i18n}
            items={AttestationValues}
            renderItem={itemValue => i18n.getString(ChileWHTConfirmValueToLabel[itemValue])}
            name="ChileWHTConfirmRadioGroup"
            validationProps={{ i18n: validationI18n, field: 'IsWithholdingTaxExempted', alwaysShowError }}
          />
        </div>
      </CustomizedFormGroup>
    </ValidationGroup>
  );
};

ChileTaxForm.propTypes = TaxTypeFormPropTypes;

const pilotFlag = requiredFeatures.ABLChileTax;
const TaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: ChileTaxForm, DefaultForm: EmptyTaxForm });
const ViewTaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: CLViewTaxForm, DefaultForm: EmptyViewTaxForm });

registerTaxform('/CL/', TaxForm, withViewTaxFormWithFilledEmptyTaxValue({ ViewTaxForm, getTaxSchema: () => ChileTaxSchema }));
